import { useCallback, useEffect, useRef, useState } from "react";
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Text,
	useDisclosure,
	Spinner,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiEdit2, FiAperture } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { cn } from "utils/helpers";
import type {
	QuoteTemplate,
	SaveQuoteTemplatePayload,
} from "../../api/quote-templates-api";
import {
	getQuoteTemplates,
	createQuoteTemplate,
	updateQuoteTemplate,
	deleteQuoteTemplate,
} from "../../api";

export interface QuoteTemplateSettingsProps {
	companyId?: number;
}

interface TemplateColumnDraft {
	id: number;
	columnType: string;
	label: string;
	sortOrder: number;
}

interface TemplateRowDraft {
	id: number;
	name: string;
	unitId: number | null;
	sortOrder: number;
}

const COLUMN_TYPE_OPTIONS = [
	{ value: "response_value", label: "Response Value" },
	{ value: "response_quantity", label: "Response Quantity" },
	{ value: "response_number", label: "Response Number" },
	{ value: "response_text", label: "Response Text" },
	{ value: "description", label: "Description" },
	{ value: "total", label: "Total" },
];

const cellBorder = "border border-[#EAECF0]";

const generateLocalId = (): number => {
	return -Math.floor(Math.random() * 1_000_000_000);
};

const QuoteTemplateSettings: React.FC<QuoteTemplateSettingsProps> = ({
	companyId,
}) => {
	const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const [editingTemplate, setEditingTemplate] = useState<QuoteTemplate | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	const [draftName, setDraftName] = useState("");
	const [draftColumns, setDraftColumns] = useState<TemplateColumnDraft[]>([]);
	const [draftRows, setDraftRows] = useState<TemplateRowDraft[]>([]);

	const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);

	const { values: unitsAndMeasures } = useUnitsAndMeasures();

	const fetchTemplates = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getQuoteTemplates();
			setTemplates(data ?? []);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	const resetDraft = useCallback(() => {
		setDraftName("");
		setDraftColumns([]);
		setDraftRows([]);
		setEditingTemplate(null);
		setIsCreating(false);
	}, []);

	const handleStartCreate = useCallback(() => {
		setIsCreating(true);
		setEditingTemplate(null);
		setDraftName("");
		setDraftColumns([
			{
				id: generateLocalId(),
				columnType: "response_value",
				label: "Default",
				sortOrder: 1,
			},
		]);
		setDraftRows([]);
	}, []);

	const handleStartEdit = useCallback((template: QuoteTemplate) => {
		setEditingTemplate(template);
		setIsCreating(false);
		setDraftName(template.name);
		setDraftColumns(
			template.columns.map((c) => ({
				id: c.id,
				columnType: c.column_type,
				label: c.label,
				sortOrder: c.sort_order,
			}))
		);
		setDraftRows(
			template.rows.map((r) => ({
				id: r.id,
				name: r.name,
				unitId: r.unit_id,
				sortOrder: r.sort_order,
			}))
		);
	}, []);

	const handleAddColumn = useCallback(() => {
		const newCol: TemplateColumnDraft = {
			id: generateLocalId(),
			columnType: "response_value",
			label: `Column ${draftColumns.length + 1}`,
			sortOrder: draftColumns.length + 1,
		};
		setDraftColumns([...draftColumns, newCol]);
	}, [draftColumns]);

	const handleRemoveColumn = useCallback(
		(colId: number) => {
			setDraftColumns(draftColumns.filter((c) => c.id !== colId));
		},
		[draftColumns]
	);

	const handleColumnChange = useCallback(
		(colId: number, field: string, value: string) => {
			setDraftColumns(
				draftColumns.map((c) =>
					c.id === colId ? { ...c, [field]: value } : c
				)
			);
		},
		[draftColumns]
	);

	const handleAddRow = useCallback(() => {
		const newRow: TemplateRowDraft = {
			id: generateLocalId(),
			name: "",
			unitId: null,
			sortOrder: draftRows.length + 1,
		};
		setDraftRows([...draftRows, newRow]);
	}, [draftRows]);

	const handleRemoveRow = useCallback(
		(rowId: number) => {
			setDraftRows(draftRows.filter((r) => r.id !== rowId));
		},
		[draftRows]
	);

	const handleRowChange = useCallback(
		(rowId: number, field: string, value: string | number | null) => {
			setDraftRows(
				draftRows.map((r) =>
					r.id === rowId ? { ...r, [field]: value } : r
				)
			);
		},
		[draftRows]
	);

	const handleSave = useCallback(async () => {
		if (!draftName.trim()) {
			return;
		}
		setIsSaving(true);
		try {
			const payload: SaveQuoteTemplatePayload = {
				name: draftName.trim(),
				columns: draftColumns.map((c) => ({
					column_type: c.columnType,
					label: c.label,
					sort_order: c.sortOrder,
				})),
				rows: draftRows.map((r) => ({
					name: r.name,
					unit_id: r.unitId,
					sort_order: r.sortOrder,
				})),
			};

			if (editingTemplate) {
				await updateQuoteTemplate(editingTemplate.id, payload);
			} else {
				await createQuoteTemplate(payload);
			}

			resetDraft();
			await fetchTemplates();
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsSaving(false);
		}
	}, [draftName, draftColumns, draftRows, editingTemplate, resetDraft, fetchTemplates]);

	const handleDeleteConfirm = useCallback(
		(templateId: number) => {
			setDeleteTargetId(templateId);
			onOpen();
		},
		[onOpen]
	);

	const handleDelete = useCallback(async () => {
		if (deleteTargetId === null) {
			return;
		}
		setIsDeleting(true);
		try {
			await deleteQuoteTemplate(deleteTargetId);
			if (editingTemplate?.id === deleteTargetId) {
				resetDraft();
			}
			await fetchTemplates();
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			setDeleteTargetId(null);
			onClose();
		}
	}, [deleteTargetId, editingTemplate, resetDraft, fetchTemplates, onClose]);

	const isEditorOpen = isCreating || editingTemplate !== null;

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-12"
				data-testid="templates-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div data-testid="quote-template-settings">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Text className="font-roboto text-lg font-semibold text-gray-900">
						Quote Templates
					</Text>
					<Text className="text-sm text-gray-500">
						Create reusable templates to quickly set up quote tables with
						predefined columns and rows.
					</Text>
				</div>
				{!isEditorOpen && (
					<Button
						size="sm"
						btntype="primary"
						onClick={handleStartCreate}
						data-testid="create-template-btn"
					>
						<FiPlus className="mr-1" />
						New Template
					</Button>
				)}
			</div>

			{!isEditorOpen && templates.length === 0 && (
				<div
					className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-12"
					data-testid="no-templates"
				>
					<FiAperture className="mb-3 text-gray-400" size={32} />
					<Text className="text-sm text-gray-500">
						No templates yet. Create one to get started.
					</Text>
				</div>
			)}

			{!isEditorOpen && templates.length > 0 && (
				<div className="space-y-2" data-testid="template-list">
					{templates.map((template) => (
						<div
							key={template.id}
							className="flex items-center justify-between rounded-lg border border-[#EAECF0] bg-white px-4 py-3 shadow-sm"
							data-testid={`template-item-${template.id}`}
						>
							<div className="flex items-center gap-3">
								<FiAperture className="text-primary-500" size={18} />
								<div>
									<Text className="font-roboto text-sm font-medium text-gray-900">
										{template.name}
									</Text>
									<Text className="text-xs text-gray-500">
										{template.columns.length} columns, {template.rows.length} rows
									</Text>
								</div>
							</div>
							<div className="flex items-center gap-1">
								<button
									type="button"
									className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
									onClick={() => handleStartEdit(template)}
									title="Edit template"
									aria-label={`Edit template ${template.name}`}
									data-testid={`edit-template-${template.id}`}
								>
									<FiEdit2 size={14} />
								</button>
								<button
									type="button"
									className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600"
									onClick={() => handleDeleteConfirm(template.id)}
									title="Delete template"
									aria-label={`Delete template ${template.name}`}
									data-testid={`delete-template-${template.id}`}
								>
									<FiTrash2 size={14} />
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			{isEditorOpen && (
				<div
					className="rounded-xl border border-[#EAECF0] bg-white shadow-sm"
					data-testid="template-editor"
				>
					<div className="border-b border-[#EAECF0] px-4 py-3">
						<Text className="font-roboto text-sm font-semibold text-gray-900">
							{editingTemplate ? "Edit Template" : "New Template"}
						</Text>
					</div>

					<div className="px-4 py-3">
						<div className="mb-4">
							<label className="mb-1 block text-xs font-medium text-gray-700">
								Template Name
							</label>
							<input
								type="text"
								className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
								value={draftName}
								onChange={(e) => setDraftName(e.target.value)}
								placeholder="e.g. Standard Construction Quote"
								aria-label="Template name"
								data-testid="template-name-input"
							/>
						</div>

						<div className="mb-4">
							<div className="mb-2 flex items-center justify-between">
								<Text className="text-xs font-medium text-gray-700">
									Columns
								</Text>
								<Button
									size="xs"
									btntype="secondary"
									onClick={handleAddColumn}
									data-testid="add-template-col-btn"
								>
									<FiPlus className="mr-1" />
									Add Column
								</Button>
							</div>
							{draftColumns.length > 0 && (
								<div className="space-y-1">
									{draftColumns.map((col) => (
										<div
											key={col.id}
											className="flex items-center gap-2"
											data-testid={`template-col-${col.id}`}
										>
											<input
												type="text"
												className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
												value={col.label}
												onChange={(e) =>
													handleColumnChange(col.id, "label", e.target.value)
												}
												placeholder="Column label"
												aria-label="Column label"
												data-testid={`template-col-label-${col.id}`}
											/>
											<select
												className="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
												value={col.columnType}
												onChange={(e) =>
													handleColumnChange(col.id, "columnType", e.target.value)
												}
												aria-label="Column type"
												data-testid={`template-col-type-${col.id}`}
											>
												{COLUMN_TYPE_OPTIONS.map((opt) => (
													<option key={opt.value} value={opt.value}>
														{opt.label}
													</option>
												))}
											</select>
											<button
												type="button"
												className="rounded p-1 text-red-400 hover:text-red-600"
												onClick={() => handleRemoveColumn(col.id)}
												title="Remove column"
												aria-label={`Remove column ${col.label}`}
												data-testid={`remove-template-col-${col.id}`}
											>
												<FiTrash2 size={12} />
											</button>
										</div>
									))}
								</div>
							)}
						</div>

						<div className="mb-4">
							<div className="mb-2 flex items-center justify-between">
								<Text className="text-xs font-medium text-gray-700">
									Rows (Scope Items)
								</Text>
								<Button
									size="xs"
									btntype="secondary"
									onClick={handleAddRow}
									data-testid="add-template-row-btn"
								>
									<FiPlus className="mr-1" />
									Add Row
								</Button>
							</div>
							{draftRows.length > 0 && (
								<div className="overflow-x-auto rounded-lg border border-[#EAECF0]">
									<table
										className="w-full table-auto"
										data-testid="template-rows-table"
									>
										<caption className="sr-only">Template row items</caption>
										<thead>
											<tr className="bg-[#F9FAFB]">
												<th
													className={cn(
														cellBorder,
														"w-10 px-2 py-2 text-center font-roboto text-xs font-medium text-tertiary-600"
													)}
												>
													#
												</th>
												<th
													className={cn(
														cellBorder,
														"px-2 py-2 font-roboto text-xs font-medium text-tertiary-600"
													)}
												>
													Item Name
												</th>
												<th
													className={cn(
														cellBorder,
														"px-2 py-2 font-roboto text-xs font-medium text-tertiary-600"
													)}
													style={{ minWidth: "140px" }}
												>
													Unit of Measurement
												</th>
												<th className="w-10 bg-[#F9FAFB]" />
											</tr>
										</thead>
										<tbody>
											{draftRows.map((row, idx) => (
												<tr
													key={row.id}
													className="border-b border-[#EAECF0]"
												>
													<td
														className={cn(
															cellBorder,
															"text-center font-roboto text-xs text-primary-500 bg-gray-50"
														)}
													>
														{idx + 1}
													</td>
													<td className={cn(cellBorder, "px-1 py-1")}>
														<input
															type="text"
															className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
															value={row.name}
															onChange={(e) =>
																handleRowChange(row.id, "name", e.target.value)
															}
															placeholder="Item name"
															aria-label={`Item name for row ${idx + 1}`}
															data-testid={`template-row-name-${row.id}`}
														/>
													</td>
													<td className={cn(cellBorder, "px-1 py-1")}>
														<select
															className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
															value={row.unitId ?? ""}
															onChange={(e) =>
																handleRowChange(
																	row.id,
																	"unitId",
																	e.target.value ? Number(e.target.value) : null
																)
															}
															aria-label={`Unit of measurement for row ${idx + 1}`}
															data-testid={`template-row-unit-${row.id}`}
														>
															<option value="">Select unit</option>
															{unitsAndMeasures?.map((unit) => (
																<option key={unit.value} value={unit.value}>
																	{unit.label}
																</option>
															))}
														</select>
													</td>
													<td className="px-1">
														<button
															type="button"
															className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
															onClick={() => handleRemoveRow(row.id)}
															title="Remove row"
															aria-label={`Remove row ${idx + 1}`}
															data-testid={`remove-template-row-${row.id}`}
														>
															<FiTrash2 size={12} />
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>

					<div className="flex items-center justify-end gap-2 border-t border-[#EAECF0] px-4 py-3">
						<Button
							size="sm"
							btntype="secondary"
							onClick={resetDraft}
							data-testid="cancel-template-btn"
						>
							Cancel
						</Button>
						<Button
							size="sm"
							btntype="primary"
							onClick={handleSave}
							disabled={isSaving || !draftName.trim()}
							data-testid="save-template-btn"
						>
							{isSaving
								? "Saving..."
								: editingTemplate
									? "Update Template"
									: "Create Template"}
						</Button>
					</div>
				</div>
			)}

			<AlertDialog
				isOpen={isOpen}
				leastDestructiveRef={cancelRef}
				onClose={onClose}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Template
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text>
								This will permanently delete this quote template. This
								action cannot be undone.
							</Text>
						</AlertDialogBody>
						<AlertDialogFooter>
							<button
								ref={cancelRef}
								onClick={onClose}
								className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
								type="button"
							>
								Cancel
							</button>
							<button
								onClick={handleDelete}
								disabled={isDeleting}
								className="ml-3 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
								type="button"
								data-testid="confirm-delete-template"
							>
								{isDeleting ? "Deleting..." : "Delete"}
							</button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</div>
	);
};

export { QuoteTemplateSettings };
